import { useCallback, useEffect, useState } from "react";

const AUTO_HIDE_MS = 5000;
// How long to wait for playback to begin before treating "not playing" as a
// real stop. Long enough to cover the mount → autoplay gap (and a swipe's
// unmount/mount swap), short enough that an autoplay-blocked video surfaces its
// controls almost immediately.
const START_GRACE_MS = 1000;

type Options = {
  /** Only run the immersive behaviour while a video is actually shown. */
  active: boolean;
  isPlaying: boolean;
  /** Reset to the immersive (hidden) state when the viewed item changes. */
  itemKey: string | undefined;
  delayMs?: number;
  graceMs?: number;
};

/**
 * Immersive-controls visibility for the viewer's video UI.
 *
 * Rules (standard mobile-player behaviour):
 * - While PLAYING, controls start hidden (an autoplaying video opens clean),
 *   a tap toggles them, and any shown state auto-hides after `delayMs`.
 * - While genuinely STOPPED — the user paused, playback ended, or autoplay
 *   never started — controls are pinned visible and never auto-hide.
 * - `poke()` shows the controls and restarts the timer (call it on any
 *   interaction with them, so scrubbing keeps them alive).
 * - Inactive (image/PDF): always visible — those keep the persistent bar.
 *
 * "Genuinely stopped" is the subtle part: `isPlaying === false` is ALSO true for
 * the transient before playback begins — including the gap while swiping, when
 * the outgoing <video> unmounts (element → null → isPlaying false) before the
 * incoming one autoplays. Pinning on that flashed the bar on for the length of
 * every swap, and on every open. So a stop only counts once playback has
 * actually begun for this item (`hasPlayed`), or once a short grace window has
 * passed without it starting (`graceElapsed`) — which is what still surfaces the
 * controls when iOS blocks autoplay, so the user can press play.
 */
export function useAutoHideControls({
  active,
  isPlaying,
  itemKey,
  delayMs = AUTO_HIDE_MS,
  graceMs = START_GRACE_MS,
}: Options) {
  // Hidden-while-playing is the only stored bit; a genuine stop always shows.
  // Starts true so an autoplaying video opens without controls.
  const [hiddenDuringPlay, setHiddenDuringPlay] = useState(true);
  // Has playback actually begun for THIS item?
  const [hasPlayed, setHasPlayed] = useState(false);
  // Did the start-grace window pass without playback beginning?
  const [graceElapsed, setGraceElapsed] = useState(false);
  // Bumped to restart the auto-hide timer without changing visibility.
  const [pokeTick, setPokeTick] = useState(0);

  // New item → back to the immersive default, and re-arm the start detection.
  useEffect(() => {
    setHiddenDuringPlay(true);
    setHasPlayed(false);
    setGraceElapsed(false);
  }, [itemKey]);

  useEffect(() => {
    if (isPlaying) {
      setHasPlayed(true);
    }
  }, [isPlaying]);

  // Arm the grace window while we're still waiting for playback to begin.
  useEffect(() => {
    if (!active || isPlaying || hasPlayed) {
      return;
    }
    const timer = setTimeout(() => setGraceElapsed(true), graceMs);
    return () => clearTimeout(timer);
  }, [active, isPlaying, hasPlayed, itemKey, graceMs]);

  // A real stop: not playing, and playback either already ran or never came.
  const stopped = !isPlaying && (hasPlayed || graceElapsed);
  const visible = !active || stopped || !hiddenDuringPlay;

  useEffect(() => {
    if (!active || !isPlaying || hiddenDuringPlay) {
      return;
    }
    const timer = setTimeout(() => setHiddenDuringPlay(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, isPlaying, hiddenDuringPlay, pokeTick, delayMs]);

  const poke = useCallback(() => {
    setHiddenDuringPlay(false);
    setPokeTick((t) => t + 1);
  }, []);

  const toggle = useCallback(() => {
    if (stopped) {
      // Pinned visible; a tap must not strand the user with a stopped video and
      // no way to resume.
      return;
    }
    setHiddenDuringPlay((hidden) => !hidden);
    setPokeTick((t) => t + 1);
  }, [stopped]);

  return { visible, toggle, poke };
}
